import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, COUNTRY_FLAGS } from "@/lib/api";
import { toast } from "sonner";
import {
  Zap, LogOut, Users, Newspaper, Play, Trash2, Terminal, AlertTriangle, RefreshCw, CheckCircle2, XCircle, Mail, Send,
} from "lucide-react";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [running, setRunning] = useState(false);
  const [runCountry, setRunCountry] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);

  const logout = () => { localStorage.removeItem("bess_admin_token"); nav("/admin/login"); };

  const load = useCallback(async () => {
    try {
      const [s, sub, lg, co] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/subscribers"), api.get("/admin/logs"), api.get("/countries"),
      ]);
      setStats(s.data); setSubs(sub.data.subscribers); setLogs(lg.data.logs); setCountries(co.data.countries);
      return lg.data.logs;
    } catch (err) {
      if (err.response?.status === 403) { nav("/admin/login"); }
      return null;
    }
  }, [nav]);

  const pollRun = useCallback(() => {
    setRunning(true);
    let ticks = 0;
    const poll = setInterval(async () => {
      ticks += 1;
      const lg = await load();
      const latest = lg?.[0];
      if (latest) {
        const done = (latest.results || []).length;
        const total = (latest.countries || []).length;
        setProgress({ done, total });
        if (latest.status !== "running" || ticks > 600) {
          clearInterval(poll);
          setRunning(false);
          setProgress(null);
          if (latest.status === "completed") toast.success(`Research complete — ${latest.total_items ?? 0} items across ${done} markets.`);
          else if (latest.status === "interrupted") toast.error("The run was interrupted (server restarted). Please run again.");
        }
      }
    }, 8000);
  }, [load]);

  useEffect(() => {
    if (!localStorage.getItem("bess_admin_token")) { nav("/admin/login"); return; }
    (async () => {
      const lg = await load();
      if (lg?.[0]?.status === "running") pollRun();
    })();
  }, [load, nav, pollRun]);

  const runResearch = async () => {
    setRunning(true);
    try {
      const body = runCountry ? { countries: [runCountry] } : {};
      await api.post("/admin/research/run", body);
      toast.info("Research started. This can take ~2 min per market — keep this tab open; progress updates below.");
      pollRun();
    } catch (err) {
      setRunning(false);
      if (err.response?.status === 409) {
        toast.info(err.response.data.detail || "A run is already in progress.");
        pollRun();
      } else {
        toast.error(err.response?.data?.detail || "Research failed");
      }
    }
  };

  const delSub = async (id) => {
    await api.delete(`/admin/subscribers/${id}`);
    toast.success("Subscriber removed");
    load();
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/send-test", {});
      toast.success(`Test brief sent to ${r.data.to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const sendAll = async () => {
    if (!window.confirm("Send the latest briefs to ALL subscribers now?")) return;
    setSending(true);
    try {
      const r = await api.post("/admin/send-now", {});
      toast.success(`Sent ${r.data.sent} · failed ${r.data.failed} · skipped ${r.data.skipped}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <header className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 bg-volt flex items-center justify-center"><Zap className="w-5 h-5 text-black" strokeWidth={2.5} /></span>
            <span className="font-display font-black text-lg tracking-tighter">COMMAND CENTER</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-2 border border-[#262626] transition-colors hover:bg-[#111]" data-testid="refresh-button"><RefreshCw className="w-3 h-3" /> Refresh</button>
            <button onClick={logout} className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-3 py-2 border border-[#262626] transition-colors hover:bg-signal hover:text-white hover:border-signal" data-testid="logout-button"><LogOut className="w-3 h-3" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {stats && !stats.key_configured && (
          <div className="mb-6 border-2 border-signal p-4 flex items-start gap-3 bg-signal/10" data-testid="key-warning">
            <AlertTriangle className="w-5 h-5 text-signal shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-display font-bold">ANTHROPIC_API_KEY not configured.</p>
              <p className="text-[#A3A3A3]">Live web-search research is disabled until you add your Anthropic API key to <span className="font-mono text-volt">backend/.env</span>. Everything else (signups, archive, scheduling) works.</p>
            </div>
          </div>
        )}

        {stats && !stats.email_configured && (
          <div className="mb-6 border-2 border-signal p-4 flex items-start gap-3 bg-signal/10" data-testid="email-warning">
            <AlertTriangle className="w-5 h-5 text-signal shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-display font-bold">RESEND_API_KEY not configured.</p>
              <p className="text-[#A3A3A3]">Email delivery is disabled until you add your Resend API key to <span className="font-mono text-volt">backend/.env</span>. Briefs still generate and appear in the archive.</p>
            </div>
          </div>
        )}

        {/* Stat bento */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Subscribers" value={stats?.subscribers ?? "—"} testid="stat-subscribers" />
          <StatCard icon={Newspaper} label="Briefs Published" value={stats?.newsletters ?? "—"} testid="stat-newsletters" />
          <StatCard icon={Zap} label="AI Key" value={stats?.key_configured ? "ACTIVE" : "MISSING"} accent={stats?.key_configured} testid="stat-key" />
          <StatCard icon={Mail} label="Email" value={stats?.email_configured ? "READY" : "MISSING"} accent={stats?.email_configured} testid="stat-email" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Research trigger */}
          <div className="border border-[#262626] p-6">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A3A3A3] mb-4 flex items-center gap-2"><Play className="w-3 h-3" /> Trigger Research</div>
            <p className="text-sm text-[#A3A3A3] mb-4">Runs live Claude web-search across the last 14 days and publishes briefs. Auto-runs every other Monday 08:00 CET (then emails subscribers).</p>
            <select value={runCountry} onChange={(e) => setRunCountry(e.target.value)} className="w-full bg-[#111] border-2 border-[#262626] h-11 px-3 font-mono text-sm mb-4 focus:ring-2 focus:ring-volt outline-none" data-testid="run-country-select">
              <option value="">All 17 markets</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <button onClick={runResearch} disabled={running} className="w-full h-12 bg-volt text-black font-display font-bold uppercase tracking-tight flex items-center justify-center gap-2 transition-colors hover:bg-white disabled:opacity-50" data-testid="run-research-button">
              {running ? <><RefreshCw className="w-4 h-4 animate-spin" /> {progress ? `Researching ${progress.done}/${progress.total}…` : "Researching…"}</> : <><Play className="w-4 h-4" /> Run now</>}
            </button>
            {running && <p className="mt-3 text-xs text-[#A3A3A3] font-mono">Keep this tab open. A full 17-market run takes ~25–35 minutes.</p>}
          </div>

          {/* Email delivery */}
          <div className="border border-[#262626] p-6">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A3A3A3] mb-4 flex items-center gap-2"><Mail className="w-3 h-3" /> Email Delivery</div>
            <p className="text-sm text-[#A3A3A3] mb-4">Send the latest briefs now (each subscriber gets their selected countries). Or send a test preview to yourself first.</p>
            <button onClick={sendTest} disabled={sending} className="w-full h-11 mb-3 border-2 border-volt text-volt font-display font-bold uppercase tracking-tight flex items-center justify-center gap-2 transition-colors hover:bg-volt hover:text-black disabled:opacity-50" data-testid="send-test-button">
              <Send className="w-4 h-4" /> Send test to me
            </button>
            <button onClick={sendAll} disabled={sending} className="w-full h-12 bg-volt text-black font-display font-bold uppercase tracking-tight flex items-center justify-center gap-2 transition-colors hover:bg-white disabled:opacity-50" data-testid="send-all-button">
              {sending ? <><RefreshCw className="w-4 h-4 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4" /> Send to all subscribers</>}
            </button>
            {stats?.last_email && (
              <div className="mt-4 font-mono text-xs text-[#A3A3A3]" data-testid="last-email">
                last send: {(stats.last_email.sent_at || "").slice(0, 16).replace("T", " ")} · sent {stats.last_email.sent} · failed {stats.last_email.failed} · skipped {stats.last_email.skipped}
              </div>
            )}
          </div>

          {/* Research logs terminal */}
          <div className="border border-[#262626] p-6">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A3A3A3] mb-4 flex items-center gap-2"><Terminal className="w-3 h-3" /> Research Logs</div>
            <div className="bg-black border border-[#262626] p-4 h-64 overflow-auto font-mono text-xs space-y-2" data-testid="research-logs">
              {logs.length === 0 && <div className="text-[#555]">$ no runs yet…</div>}
              {logs.map((l) => (
                <div key={l.id} className="border-b border-[#1a1a1a] pb-2">
                  <div className="text-volt">$ run {l.triggered_by} · {l.status} · {(l.started_at || "").slice(0, 16).replace("T", " ")}</div>
                  <div className="text-[#A3A3A3]">window {l.window?.start} → {l.window?.end} · total {l.total_items ?? "?"}</div>
                  {(l.results || []).map((r, i) => (
                    <div key={i} className={r.status === "ok" ? "text-[#888]" : "text-signal"}>
                      {r.status === "ok" ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
                      {r.country} → {r.status === "ok" ? `${r.count} items` : (r.error || "error").slice(0, 60)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Per-country table */}
        <div className="border border-[#262626] mt-4">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A3A3A3] p-4 border-b border-[#262626]">Market Coverage</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-[#262626]">
            {(stats?.per_country || []).map((c) => (
              <div key={c.code} className="bg-[#0A0A0A] p-4">
                <div className="font-display font-bold">{COUNTRY_FLAGS[c.code]} {c.name}</div>
                <div className="font-mono text-xs text-[#A3A3A3] mt-1">{c.subscribers} subs · {c.newsletters} briefs</div>
              </div>
            ))}
          </div>
        </div>

        {/* Subscribers */}
        <div className="border border-[#262626] mt-4">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A3A3A3] p-4 border-b border-[#262626] flex items-center justify-between">
            <span>Subscribers ({subs.length})</span>
          </div>
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead>
              <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-[#737373] border-b border-[#262626]">
                <th className="p-3">Email</th><th className="p-3">Markets</th><th className="p-3">Since</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {subs.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[#555] font-mono text-xs">No subscribers yet</td></tr>}
              {subs.map((s) => (
                <tr key={s.id} className="border-b border-[#1a1a1a]" data-testid={`sub-row-${s.id}`}>
                  <td className="p-3 font-mono">{s.email}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {s.countries.map((c) => <span key={c} className="font-mono text-[10px] border border-[#262626] px-1.5 py-0.5">{c}</span>)}
                    </div>
                  </td>
                  <td className="p-3 font-mono text-xs text-[#737373]">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => delSub(s.id)} className="text-[#737373] transition-colors hover:text-signal" data-testid={`delete-sub-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="border border-[#262626] p-5" data-testid={testid}>
    <Icon className={`w-5 h-5 mb-4 ${accent ? "text-volt" : "text-[#A3A3A3]"}`} />
    <div className="font-display font-black text-2xl tracking-tighter">{value}</div>
    <div className="text-[11px] uppercase tracking-[0.2em] text-[#737373] mt-1">{label}</div>
  </div>
);
